import createContainer from 'constate';
import { useState } from 'react';
import { setPromotionalProductToAdd } from './CartHelpers';
import {
  addCollectionCommerce,
  addProductCommerce,
  removeCollectionCommerce,
  removeProductCommerce,
  setWarrantyAndServices,
} from './cart';
import { nameHOC } from './hocutils';

function useCartContextHook() {
  const [cart, setCart] = useState({
    bundleItems: [],
  });
  const [showCart, setShowCart] = useState(false);
  const [isSizeSelected, setSizeSelection] = useState(false);
  const [isVariantSelectionVisible, setVariantSelectionVisible] = useState(true);
  const [quantityWhileVariantSelectionVisible, setQuantityWhileVariantSelectionVisible] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [warrantyOptionCurrent, setWarrantyOptionCurrent] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showExtendedWarrantyModal, setShowExtendedWarrantyModal] = useState(false);
  const [pendingProductData, setPendingProductData] = useState(null);

  const setWarrantyOptionMethod = (payload) => {
    return setWarrantyAndServices({
      ...payload,
      channel: 'warranty',
    });
  };

  const continueAfterWarranty = (warrantyOption) => {
    if (!pendingProductData) {
      return Promise.resolve();
    }
    let promise;
    if (pendingProductData.addFunction) {
      const addToCartFunction = pendingProductData.addFunction;
      const { params } = pendingProductData;
      if (warrantyOption) {
        params.warranty = warrantyOption;
      }
      promise = addToCartFunction(params);
    } else if (pendingProductData.product) {
      if (!warrantyOption) {
        promise = Promise.resolve();
      } else {
        promise = setWarrantyOptionMethod({
          cart,
          setCart,
        })({
          appCtx: pendingProductData.appCtx,
          item: {
            commerceItemId: pendingProductData.product.commerceItemId,
            skuId: warrantyOption.value || warrantyOption.variantId,
          },
          productContext: pendingProductData.product,
          variantId: pendingProductData.product.skuId,
          offeringId: warrantyOption.offeringId,
          totalQuantity: pendingProductData.product.quantity || 1,
        });
      }
    } else {
      promise = Promise.resolve();
    }
    return promise
      .then(() => {
        setShowCart(true);
        setPendingProductData(null);
        return Promise.resolve();
      })
      .catch((error) => {
        setShowCart(true);
        setPendingProductData(null);
        return Promise.reject(error);
      });
  };

  return {
    cart: {
      ...cart,
      isSizeSelected,
      setSizeSelection,
      isVariantSelectionVisible,
      setVariantSelectionVisible,
      quantityWhileVariantSelectionVisible,
      setQuantityWhileVariantSelectionVisible,
      selectedSize,
      setSelectedSize,
      selectedOperatorId,
      setSelectedOperatorId,
      addProduct: addProductCommerce({
        cart,
        setCart,
        setShowCart,
        setVariantSelectionVisible,
        setQuantityWhileVariantSelectionVisible,
      }),
      removeProduct: removeProductCommerce({
        cart,
        setCart,
        setShowCart,
      }),
      setWarrantyOption: setWarrantyOptionMethod({
        cart,
        setCart,
      }),
      addCollection: addCollectionCommerce({
        cart,
        setCart,
        setShowCart,
        setVariantSelectionVisible,
        setQuantityWhileVariantSelectionVisible,
      }),
      removeCollection: removeCollectionCommerce({
        cart,
        setCart,
        setShowCart,
      }),
      setPromotionalProductToAdd: setPromotionalProductToAdd({ cart, setCart }),

      setAssemblyOption: setWarrantyAndServices({
        cart,
        setCart,
        channel: 'service',
      }),
      showCart,
      setShowCart,
      warrantyOptionCurrent,
      setWarrantyOptionCurrent,
      showLoginForm,
      setShowLoginForm,
      showExtendedWarrantyModal,
      setShowExtendedWarrantyModal,
      pendingProductData,
      setPendingProductData,
      continueAfterWarranty,
    },
  };
}

const [CartContextProvider, useCartContext] = createContainer(useCartContextHook);

const withCart = (WrappedComponent) => {
  const { getInitialProps } = WrappedComponent;
  const ComponentWithCartCtx = (props) => {
    const { cart } = useCartContext();

    return <WrappedComponent {...props} cart={cart} />;
  };
  if (typeof getInitialProps === 'function') {
    ComponentWithCartCtx.getInitialProps = getInitialProps;
  }
  ComponentWithCartCtx.originalName = nameHOC(WrappedComponent);
  ComponentWithCartCtx.displayName = nameHOC(WrappedComponent, 'WithCartContext');

  return ComponentWithCartCtx;
};

export { withCart, CartContextProvider, useCartContext };
